// src/pages/Following/FollowingListMobile.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../shared/contexts/AuthContext";
import api from "../../../shared/api/axios";
import FollowButtonMobile from "../../components/FollowButtonMobile";
import { useTranslation } from "react-i18next";

interface LiteUser {
  _id: string;
  profilename: string;
  picture?: string;
  createdAt?: string;
}

interface FollowRow {
  following: LiteUser;     // the person you follow
  createdAt: string;       // when you followed them (if provided)
}

interface ApiFollowingResponse {
  users?: LiteUser[];
  items?: FollowRow[];     // or items with { following, createdAt }
}


const FollowingListMobile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [entries, setEntries] = useState<FollowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(""); // search query

  // for per-row actions
  const [actionUserId, setActionUserId] = useState<string | null>(null);
  const [blockBusyId, setBlockBusyId] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";
  const getAvatarUrl = (path?: string) =>
    path ? (path.startsWith("http") ? path : `${baseUrl}${path}`) : "";

  // Normalize server response into FollowRow[] (preserve server order)
  const normalize = (data: ApiFollowingResponse): FollowRow[] => {
    if (Array.isArray(data.items) && data.items.length) {
      return data.items
        .filter((r) => r?.following?._id)
        .map((r) => ({
          following: {
            _id: r.following._id,
            profilename: r.following.profilename,
            picture: r.following.picture,
          },
          createdAt: r.createdAt,
        }));
    }

    if (Array.isArray(data.users) && data.users.length) {
      return data.users
        .filter((u) => u?._id)
        .map((u) => ({
          following: {
            _id: u._id,
            profilename: u.profilename,
            picture: u.picture,
          },
          createdAt: (u as any).createdAt || "",
        }));
    }

    return [];
  };

  useEffect(() => {
    const fetchFollowing = async () => {
      if (!user?._id) return;
      try {
        setLoading(true);
        const res = await api.get<ApiFollowingResponse>(
          `/follows/${user._id}/following`,
          {
            params: { offset: 0, limit: 500, includeMeta: 1 },
          }
        );
        
        setEntries(normalize(res.data));
      } catch (err) {
        console.error("❌ Failed to fetch following list:", err);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowing();
  }, [user?._id]);

  // Filter by search query (case-insensitive). No sorting — keep server order.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((r) =>
      r.following.profilename.toLowerCase().includes(term)
    );
  }, [entries, q]);

  // --- Block helpers (same logic as SidebarVisitor, adapted here) ---

  const [confirmState, setConfirmState] = useState<{
  mode: "block" | "report";
  userId: string;
  profilename?: string;
} | null>(null);

const blockUser = async (userId: string, profilename?: string) => {
  setConfirmState({ mode: "block", userId, profilename });
};

const blockAndReportUser = async (userId: string, profilename?: string) => {
  setConfirmState({ mode: "report", userId, profilename });
};


  // Close dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e: MouseEvent) => {
    if (!actionUserId) return; // nothing open

    if (
      menuRef.current &&
      !menuRef.current.contains(e.target as Node)
    ) {
      setActionUserId(null);
    }
  };

document.addEventListener("click", handleClickOutside);
  return () => {
document.removeEventListener("click", handleClickOutside);
  };
}, [actionUserId]);

  return (
    <div style={pageWrap}>
     
      {/* Right content area */}
      <div style={contentCol}>
        <div style={{ marginTop: "0%" }}>
          <h2 style={{ color: "#ccc", margin: 0 }}>{t('followingList.title')}</h2>

          {/* 🔎 Search below title */}
          <div style={{ marginTop: 20, maxWidth: 420 }}>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('followingList.searchPlaceholder')}
              aria-label={t('followingList.searchAria')}
              style={searchInput}
            />
          </div>
        </div>

        {loading ? (
          <p style={{ color: "#aaa", marginTop: 20 }}>{t('followingList.loading')}</p>
        ) : filtered.length === 0 ? (
          <p style={{ color: "#aaa", marginTop: 20 }}>
            {q
      ? t('followingList.emptyWithQuery')
      : t('followingList.emptyNoQuery')}
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              marginTop: 22,
            }}
          >
            {filtered.map((row) => {
              const u = row.following;
              const avatar = getAvatarUrl(u.picture);
              const hasDate = !!row.createdAt;
              const open = actionUserId === u._id;
              const busy = blockBusyId === u._id;

              return (
                <div
                  key={u._id}
                  style={itemRow}
                  onClick={() =>
                    navigate(`/visitor-profile/${u._id}`)
                  }
                >
                  <div style={avatarStyle(avatar)}>
                    {!u.picture && "👻"}

                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    <span style={{ color: "#bbb", fontWeight: 600, fontSize: 14 }}>

                      {u.profilename}
                    </span>
                    <span style={{ color: "#999", fontSize: 10 }}>
  {hasDate
    ? t('followingList.followedOn', {
        date: `${new Date(row.createdAt).toLocaleDateString()} ${new Date(
          row.createdAt
        ).toLocaleTimeString()}`,
      })
    : t('followingList.followedOnUnknown')}
</span>

                  </div>

                  {/* CTA cluster (Follow/Unfollow + View + 3-dots menu) */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      position: "relative",
                    }}
                      ref={open ? menuRef : undefined}

                  >
                 <div
  onClick={(e) => e.stopPropagation()}
  style={{ transform: "scale(1.05)", transformOrigin: "center" }}
>
  <FollowButtonMobile
    authorId={u._id}
    profilename={u.profilename}
  />
</div>


                    <button
                      style={viewBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/visitor-profile/${u._id}`);
                      }}
                    >
                      {t('followingList.viewProfile')}
                    </button>

                    {/* ⋮ menu button on the RIGHT of View */}
                    <button
                      style={{
                        ...kebabBtn,
                        ...(busy
                          ? { opacity: 0.6, cursor: "wait" }
                          : null),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionUserId((prev) =>
                          prev === u._id ? null : u._id
                        );
                      }}
                      disabled={busy}
                      aria-label={t('followingList.moreActions')}
                    >
                      ⋮
                    </button>

                    {/* Dropdown menu */}
                    {open && (
                      <div
  style={dropdown}
  onMouseDown={(e) => e.stopPropagation()}
  onPointerUp={(e) => e.stopPropagation()}
>

                        <button
  type="button"
  style={dropdownItem}
  onPointerUp={() => blockUser(u._id, u.profilename)}
>
  🚫 {t('followers.dropdown.block')}
</button>

<button
  type="button"
  style={{ ...dropdownItem, color: "#ff6b6b" }}
  onPointerUp={() => blockAndReportUser(u._id, u.profilename)}
>
  🛑 {t('followers.dropdown.blockAndReport')}
</button>

                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

 {confirmState && (
  <div style={confirmOverlay}>
    <div style={confirmCard}>
      <div style={{ color: "#ccc", fontSize: 15, marginBottom: 14 }}>
        {t("followingList.blockConfirm", {
          name: confirmState.profilename
            ? `@${confirmState.profilename}`
            : t("followingList.blockUnnamedUser"),
        })}
      </div>

      <button
        style={primaryBtn}
        onClick={async () => {
          try {
            await api.post(`/block-report/block/${confirmState.userId}`);
            alert(t("followingList.blockSuccess"));

            if (confirmState.mode === "report") {
              navigate(
                `/report?targetType=user&targetId=${confirmState.userId}`
              );
            }
          } catch {
            alert(t("followingList.blockFailedFallback"));
          } finally {
            setConfirmState(null);
          }
        }}
      >
        ✅ {t("common.confirm")}
      </button>

      <button
        style={cancelBtn}
        onClick={() => setConfirmState(null)}
      >
        ❌ {t("common.cancel")}
      </button>
    </div>
  </div>
)}


    </div>
  );
};

/* ---------------- Styles ---------------- */

const pageWrap: React.CSSProperties = {
  display: "flex",
  backgroundColor: "#000",
  height: "100vh",
  overflow: "hidden",
};
const contentCol: React.CSSProperties = {
  flex: 1,
  padding: 14,
  overflowY: "auto",
  height: "100vh",
  boxSizing: "border-box",
};


const searchInput: React.CSSProperties = {
  width: "93%",
  padding: "8px 10px",
  borderRadius: 9,
  border: "1px solid #333",
  backgroundColor: "#111",
  color: "#ddd",
  outline: "none",
  fontSize: 16,
};


const itemRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  backgroundColor: "#1a1a1a",
  padding: "8px 10px",
  borderRadius: 10,
  cursor: "pointer",
  transition: "background 0.2s",
};


const avatarStyle = (imgUrl?: string): React.CSSProperties => ({
  width: 34,
  height: 34,
  borderRadius: 8,
  backgroundImage: imgUrl ? `url(${imgUrl})` : "none",
  backgroundColor: imgUrl ? "transparent" : "rgb(38, 29, 162)",
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: imgUrl ? 0 : 18,
  color: "#fff",
});


const baseBtn: React.CSSProperties = {
  backgroundColor: "#111",
  color: "#ccc",
  border: "1px solid #333",
  borderRadius: 8,
  padding: "8px 10px",
  cursor: "pointer",
};

const viewBtn: React.CSSProperties = {
  ...baseBtn,
  padding: "3px 7px",
  fontSize: 13,
};

/* 3-dots button */
const kebabBtn: React.CSSProperties = {
  ...baseBtn,
  padding: "4px 8px",
  fontSize: 16,
  lineHeight: 1,
};

/* Dropdown menu styles */
const dropdown: React.CSSProperties = {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: 8,
  backgroundColor: "#111",
  borderRadius: 12,
  border: "1px solid #333",
  boxShadow: "0 8px 24px rgba(0,0,0,0.7)",
  display: "flex",
  flexDirection: "column",
  minWidth: 200,     // ⬅️ bigger width
  zIndex: 100,
  overflow: "hidden",
};
const dropdownItem: React.CSSProperties = {
  padding: "14px 16px",
  background: "#111",
  color: "#eee",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  fontSize: 16,
  fontWeight: 600,
  transition: "background 0.15s ease",
};

const confirmOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const confirmCard: React.CSSProperties = {
  width: "90%",
  maxWidth: 420,
  background: "#111",
  borderRadius: 14,
  padding: 20,
  border: "1px solid #333",
  textAlign: "center",
};

const primaryBtn: React.CSSProperties = {
  width: "100%",
  padding: 14,
  borderRadius: 10,
  border: "none",
  background: "#5b21b6",
  color: "#fff",
  fontWeight: 700,
  marginBottom: 10,
};

const cancelBtn: React.CSSProperties = {
  width: "100%",
  padding: 12,
  borderRadius: 10,
  border: "none",
  background: "transparent",
  color: "#aaa",
};



export default FollowingListMobile;
